import { ConfirmAction } from "@atlas/composite";
import {
  Badge,
  Button,
  type CellContext,
  type ColumnDef,
  cn,
  DataGrid,
  DataGridColumnHeader,
  Label,
  Switch,
  ToolbarGroup,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IIdpGroup } from "@models";
import { useIdpGroups, useUpdateIdpGroup } from "@query";
import { APP_BASE_PATH, DetailsPanel } from "@shared";
import { Eye, Pencil } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";

const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
  height: "full",
  headerSticky: true,
} as const;

interface IIdpGroupsProps {
  companyUuid: string | undefined;
  onEditGroup: (group: IIdpGroup) => void;
}

export const IdpGroups = memo(function IdpGroups({
  companyUuid,
  onEditGroup,
}: IIdpGroupsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: groups = [], isLoading } = useIdpGroups(
    companyUuid,
    APP_BASE_PATH,
  );
  const [selectedGroup, setSelectedGroup] = useState<IIdpGroup | null>(null);
  const [statusDialog, setStatusDialog] = useState<{
    group: IIdpGroup;
    isActive: boolean;
  } | null>(null);

  const { mutate: updateGroup } = useUpdateIdpGroup(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: () => {
        setStatusDialog(null);
        toast.add({
          title: t("hub.administer.idp_group_status_success"),
          description: t("hub.administer.idp_group_status_success_description"),
          data: { type: "success" },
        });
      },
      onError: () => {
        toast.add({
          title: t("hub.administer.idp_group_status_error"),
          description: t("hub.administer.idp_group_status_error_description"),
          data: { type: "error" },
        });
      },
    },
  );

  const tableRows = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        description: group.description?.trim() ? group.description : "—",
        status: group.isActive === false ? "Inactive" : "Active",
        isActive: group.isActive !== false,
      })),
    [groups],
  );

  type IIdpGroupRow = (typeof tableRows)[number];

  const headerTextClassName = cn(
    "text-foreground px-1 py-0.5 font-semibold text-sm",
  );

  const statusFilterOptions = useMemo(
    () => [
      { label: t("hub.profile.tenant_active"), value: "Active" },
      { label: t("hub.profile.tenant_inactive"), value: "Inactive" },
    ],
    [t],
  );

  const columns = useMemo<ColumnDef<IIdpGroupRow, unknown>[]>(
    () => [
      {
        accessorKey: "name",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.idp_group_name")}
            className={headerTextClassName}
          />
        ),
        cell: (info) => (
          <Label className="font-medium text-sm">
            {(info.getValue() as string) ?? ""}
          </Label>
        ),
      },
      {
        accessorKey: "description",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.idp_group_description")}
            className={headerTextClassName}
          />
        ),
        cell: (info) => (
          <Label className="text-sm text-muted-foreground truncate max-w-md block">
            {(info.getValue() as string) ?? "—"}
          </Label>
        ),
      },
      {
        accessorKey: "status",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.status")}
            className={headerTextClassName}
          />
        ),
        filterFn: (row, columnId, filterValue) => {
          const values = Array.isArray(filterValue) ? filterValue : [];
          if (values.length === 0) return true;
          return values.includes(row.getValue(columnId));
        },
        cell: (info: CellContext<IIdpGroupRow, unknown>) => (
          <Badge
            variant={info.row.original.isActive ? "success" : "neutral"}
            appearance="ghost"
            size="sm"
            dot>
            {info.row.original.status}
          </Badge>
        ),
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        cell: (info) => (
          <ToolbarGroup className="gap-2">
            <Button variant="ghost" size="icon">
              <Eye className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEditGroup(info.row.original);
              }}>
              <Pencil className="size-4" />
            </Button>
            <div className="inline-flex cursor-default border-0 bg-transparent p-0">
              <Switch
                checked={info.row.original.isActive}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={(checked) => {
                  setStatusDialog({
                    group: info.row.original,
                    isActive: !!checked,
                  });
                }}
              />
            </div>
          </ToolbarGroup>
        ),
      },
    ],
    [headerTextClassName, onEditGroup, t],
  );

  const handleRowClick = useCallback((row: IIdpGroupRow) => {
    setSelectedGroup(row);
  }, []);

  const handleStatusConfirm = useCallback(() => {
    if (!statusDialog) return;
    updateGroup({
      groupId: statusDialog.group.id,
      body: {
        name: statusDialog.group.name,
        description:
          statusDialog.group.description === "—"
            ? undefined
            : statusDialog.group.description || undefined,
        isActive: statusDialog.isActive,
      },
    });
  }, [statusDialog, updateGroup]);

  const detailItems = useMemo(() => {
    if (!selectedGroup) return [];
    return [
      {
        label: t("hub.administer.idp_group_name"),
        value: selectedGroup.name,
      },
      {
        label: t("hub.administer.idp_group_description"),
        value: selectedGroup.description ?? "—",
      },
      {
        label: t("hub.administer.status"),
        value: selectedGroup.isActive === false ? "Inactive" : "Active",
      },
    ];
  }, [selectedGroup, t]);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 min-h-0">
          <DataGrid<IIdpGroupRow>
            data={tableRows}
            columns={columns}
            recordCount={tableRows.length}
            onRowClick={handleRowClick}
            tableLayout={DATA_GRID_LAYOUT}
            withExport
            withSearch={{
              searchKey: "name",
              placeholder: t("hub.administer.search_results"),
            }}
            withFilter={{
              filters: [
                {
                  columnId: "status",
                  label: t("hub.administer.status"),
                  options: statusFilterOptions,
                },
              ],
            }}
            paginationPlacement="toolbar"
            loading={isLoading}
            loadingMessage={t("hub.administer.loading")}
            emptyMessage={t("hub.administer.idp_groups_empty")}
          />
        </div>
      </div>

      <DetailsPanel
        open={!!selectedGroup}
        onOpenChange={(open) => !open && setSelectedGroup(null)}
        title={t("hub.administer.idp_group_details")}
        items={detailItems}
        statusLabel={t("hub.administer.status")}
        cancelLabel={t("hub.administer.cancel")}
        onEdit={
          selectedGroup
            ? () => {
                onEditGroup(selectedGroup);
                setSelectedGroup(null);
              }
            : undefined
        }
        editLabel={t("hub.administer.edit")}
      />

      {statusDialog && (
        <ConfirmAction
          open={!!statusDialog}
          onOpenChange={(open) => !open && setStatusDialog(null)}
          title={
            statusDialog.isActive
              ? t("hub.administer.activate_idp_group_title")
              : t("hub.administer.deactivate_idp_group_title")
          }
          description={
            statusDialog.isActive
              ? t("hub.administer.activate_idp_group_message")
              : t("hub.administer.deactivate_idp_group_message")
          }
          confirmText={
            statusDialog.isActive
              ? t("hub.administer.activate")
              : t("hub.administer.deactivate")
          }
          cancelText={t("hub.administer.cancel")}
          actionVariant={statusDialog.isActive ? "default" : "destructive"}
          onConfirm={handleStatusConfirm}
        />
      )}
    </>
  );
});
